import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
} from '@azure/msal-browser';
import { User } from 'app/core/user/user.types';
import { filter, map } from 'rxjs';
import { AuthPermissionService } from './core/auth/permissions/auth.permission.service';
import { PermissionService } from './core/auth/permissions/permission.service';
import { UserService } from './core/user/user.service';
import { NotificationService } from './shared/services/notification.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly userService = inject(UserService);
    private readonly notificationService = inject(NotificationService);
    private readonly authPermissionService = inject(AuthPermissionService);
    private readonly permissionService = inject(PermissionService);

    ngOnInit(): void {
        this.msalService
            .handleRedirectObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();

        const cachedAccount = this.getCachedAccount();
        if (cachedAccount) {
            this.initializeSessionForAccount(cachedAccount);
            return;
        }

        console.log('No cached account found.');
        this.listenForLoginSuccess();
    }

    private configureNavigationFromPermissions(user: User): void {
        this.authPermissionService
            .getPermissionsKeyValues()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (permissions) => {
                    this.permissionService.setPermissionKeys(permissions);

                    const branchId =
                        user.branchId ??
                        (Number(localStorage.getItem('branchId')) || 0);
                    const roleId =
                        user.roleId ?? localStorage.getItem('roleId') ?? '';
                    this.permissionService
                        .loadUserPermissions(branchId, roleId)
                        .subscribe();
                },
                error: (error) => {
                    console.error(
                        'Error fetching permissions key values:',
                        error
                    );
                    this.notificationService.error(
                        'Failed to load permissions key values.'
                    );
                    this.permissionService.signalPermissionsLoaded();
                },
            });
    }

    private listenForLoginSuccess(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (event: EventMessage) =>
                        event.eventType === EventType.LOGIN_SUCCESS
                ),
                map((event) => (event.payload as AuthenticationResult).account),
                filter((account): account is AccountInfo => !!account),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((account) => {
                this.initializeSessionForAccount(account, true);
            });
    }

    private initializeSessionForAccount(
        account: AccountInfo,
        showSuccessNotification: boolean = false
    ): void {
        this.msalService.instance.setActiveAccount(account);

        this.userService
            .initializeUserSessionFromAccount(account)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (user) => {
                    if (!user) {
                        this.notificationService.error(
                            'This user is not registered in the system.'
                        );
                        this.msalService.logoutRedirect({
                            postLogoutRedirectUri: '/login-failed',
                        });
                        return;
                    }

                    this.configureNavigationFromPermissions(user);

                    if (showSuccessNotification) {
                        this.notificationService.success('Login successful!');
                    }
                },
                error: (error) => {
                    console.error('Error initializing user session:', error);
                    const isBackendUnreachable =
                        error instanceof HttpErrorResponse &&
                        (error.status === 0 || error.status >= 500);
                    if (isBackendUnreachable) {
                        this.router.navigate(['/service-unavailable']);
                    } else if (error?.error?.errors?.['General.Unauthorized']?.length > 0) {
                        console.log(error.error.errors['General.Unauthorized'][0]);
                        this.router.navigate(['/login-failed'], {
                            state: {
                                message:
                                    error.error.errors[
                                    'General.Unauthorized'
                                    ][0],
                            },
                        });
                    } else {
                        this.notificationService.error(
                            'Failed to validate user.'
                        );
                        this.msalService.logoutRedirect({
                            postLogoutRedirectUri: '/login-failed',
                        });
                    }
                },
            });
    }

    private getCachedAccount(): AccountInfo | null {
        return (
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0] ??
            null
        );
    }
}
